import {
  Link,
  mediaHelper,
  paletteHelper,
  radiusHelper,
  spacingHelper,
  typographyHelper,
} from '@property.com.au/ui-kit';
import { type FunctionComponent } from 'react';
import { styled } from 'styled-components';
import {
  OutboundTrackingEventData,
  trackOutboundLinkClick,
} from '../../../analytics/event-tracking/trackOutboundLinkClick';
import {
  ApiLinkVariant,
  mapApiWeblinkVariantToLinkVariant,
} from '../../../app-kit/helpers/mapApiWeblinkVariantToLinkVariant';
import bannerImage from '../../../assets/mortgage-choice-banner.png';
import logoImage from '../../../assets/rea-mc-logo.svg';
import { PageContainer } from '../../../components/PageContainer/PageContainer';

const MORTGAGE_CHOICE_BRAND_COLOUR = '#006875';

const StyledPageContainer = styled(PageContainer)`
  display: flex;
  height: 200px;
  background: ${MORTGAGE_CHOICE_BRAND_COLOUR};
  border-radius: ${radiusHelper('large')};

  ${mediaHelper('mobile')} {
    height: 384px;
    flex-direction: column-reverse;
  }
`;

const ContentWrapper = styled.div`
  width: 50%;
  padding: ${spacingHelper('38px large')};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  ${mediaHelper('mobile')} {
    width: 100%;
    height: 50%;
    padding: ${spacingHelper('large medium')};
  }
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${spacingHelper('extraSmall')};
`;

const StyledLogo = styled.img`
  width: 178px;
  height: 40px;
`;

const StyledText = styled.div`
  color: ${paletteHelper('textInvertedStatic')};
  ${typographyHelper('body01')};
  text-align: center;
  max-width: 320px;
`;

const LinkContainer = styled.div`
  padding-top: ${spacingHelper('medium')};
`;

const StyledLink = styled(Link)`
  border-radius: ${radiusHelper('extraLarge')};
  color: ${MORTGAGE_CHOICE_BRAND_COLOUR};
  background-color: ${paletteHelper('backgroundPrimary')};
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.25rem;
  width: 162px;

  &:hover {
    color: ${MORTGAGE_CHOICE_BRAND_COLOUR};
    background-color: ${paletteHelper('backgroundSecondaryBase')};
  }

  &:focus {
    color: ${MORTGAGE_CHOICE_BRAND_COLOUR};
    background-color: ${paletteHelper('backgroundSecondary')};
  }
`;

const ImageContainer = styled.div`
  display: flex;
  width: 50%;

  ${mediaHelper('mobile')} {
    width: 100%;
    height: 50%;
    border-top-right-radius: ${radiusHelper('large')};
    border-top-left-radius: ${radiusHelper('large')};
  }
`;

const StyledImage = styled.img`
  border-top-right-radius: ${radiusHelper('large')};
  border-bottom-right-radius: ${radiusHelper('large')};

  width: 100%;
  object-fit: cover;
  object-position: top;

  ${mediaHelper('mobile')} {
    border-top-right-radius: ${radiusHelper('large')};
    border-top-left-radius: ${radiusHelper('large')};
    border-bottom-right-radius: unset;
  }
`;

interface StreetPageBannerProps {
  text: string;
  variant: ApiLinkVariant;
  title: string;
  url: string;
  outboundTracking: OutboundTrackingEventData;
}

export const StreetPageBanner: FunctionComponent<StreetPageBannerProps> = ({
  text,
  variant,
  title,
  url,
  outboundTracking,
}) => {
  const {
    schema,
    data: { event_name, type, click_url, name, context },
  } = outboundTracking;

  return (
    <StyledPageContainer>
      <ContentWrapper>
        <ContentContainer>
          <StyledLogo alt="Mortgage choice logo image" src={logoImage} />
          <StyledText>{text}</StyledText>
        </ContentContainer>
        <LinkContainer>
          <StyledLink
            href={url}
            variant={mapApiWeblinkVariantToLinkVariant(variant)}
            sizeVariant="medium"
            target="_blank"
            onClick={() =>
              trackOutboundLinkClick({
                url: click_url,
                outboundLinkType: type,
                schema,
                event_name,
                name,
                context,
              })
            }
          >
            {title}
          </StyledLink>
        </LinkContainer>
      </ContentWrapper>
      <ImageContainer>
        <StyledImage src={bannerImage} alt="Mortgage choice banner image" />
      </ImageContainer>
    </StyledPageContainer>
  );
};
